<script setup>
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useDisplay } from "vuetify"
import { normalize } from "@/utils/strings"
import { useModalStore } from "@/stores/ModalStore"
import { useTagsStore } from "@/stores/TagsStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import STag from "@/components/chips/STag.vue"
import MobileAutocomplete from "@/components/MobileAutocomplete.vue"

/**
 * Multi-select tag picker. Selected tags via v-model, available tags loaded from TagsStore by `type`.
 * Not found tag -> opens add tag dialog, "+" button -> opens tags management.
 * On mobile the field is readonly and a click opens the fullscreen MobileAutocomplete picker.
 */
const props = defineProps({
  // Backend tag type: 'events' | 'clients'
  type: {
    type: String,
    required: true,
  },

  // Readonly hides chip close buttons; disabled greys everything out
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  dontHideCloseBtnOnTags: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ type: Array, default: () => [] })

const { t } = useI18n()
const { mobile } = useDisplay()
const modalStore = useModalStore()
const tagsStore = useTagsStore()
const calendarStore = useCalendarStore()

const ADD_NEW = '__add_new__'

const search = ref('')
const autocompleteRef = ref(null)
const mobilePickerRef = ref(null)

const canCreate = computed(() => tagsStore.canCreate[props.type] && calendarStore.viewSettings.showCreateNewTagsButton)

/** Tags matching the search; offers "add new" when nothing matches */
const items = computed(() => {
  const query = normalize(search.value ?? '')
  const tags = (tagsStore.availableTags[props.type] ?? []).filter(tag => tag.search.includes(query))

  if (!tags.length && query && canCreate.value) tags.push({ id: ADD_NEW, name: t('tag_not_found') })

  return tags
})

const selectedTags = computed({
  get: () => model.value ?? [],
  set: tags => {
    if (tags.some(tag => tag.id === ADD_NEW)) {
      addNewTag(search.value)

      return
    }
    model.value = tags

    // Mobile dialog keeps the search so user can tick several filtered tags
    if (mobile.value) return
    search.value = ''

    // Clearing search re-renders the list, so a keyboard-focused item disappears and focus drops to <body>
    nextTick(() => autocompleteRef.value?.focus())
  },
})

const removeTag = id => {
  model.value = selectedTags.value.filter(tag => tag.id !== id)
}

const fetchTags = () => tagsStore.fetchTags(props.type)

// ========================================
// Add / manage tags
// ========================================
const addNewTag = async name => {
  // Fullscreen mobile picker would cover the dialog
  mobilePickerRef.value?.closeDialog()
  search.value = ''

  const tag = await modalStore.openModal('editTag', '', false, { name, type: props.type })
  const fitsType = props.type === 'events' ? tag?.forEvents : tag?.forClients

  if (fitsType) model.value = [...selectedTags.value, { id: tag.id, name: tag.name, search: tag.search, color: tag.color }]
}

const openManager = async () => {
  await fetchTags() // canCreate is known only after the first fetch
  if (canCreate.value) modalStore.openModal('tagsManager', '', false, { type: props.type })
}

// Tag edited/deleted in management dialogs -> refresh selected chips and the cached list
watch(() => tagsStore.lastChange, ({ action, tag }) => {
  if (selectedTags.value.some(item => item.id === tag.id)) {
    model.value = action === 'deleted'
      ? selectedTags.value.filter(item => item.id !== tag.id)
      : selectedTags.value.map(item => item.id === tag.id ? { ...item, name: tag.name, color: tag.color, search: tag.search } : item)
  }
  fetchTags()
})
</script>

<template>
  <div class="d-flex align-center ga-2">
    <!-- The fullscreen picker only opens on mobile; on desktop it just renders the field -->
    <MobileAutocomplete
      ref="mobilePickerRef"
      v-model="selectedTags"
      :items="items"
      item-title="name"
      item-value="id"
      :inner-label="$t('search_tag')"
      :inner-placeholder="$t('enter_tag_name')"
      :loading="tagsStore.isLoading[type]"
      :disabled="readonly || disabled"
      multiple
      return-object
      no-filter
      hide-no-data
      @update:search="search = $event"
      @blur="search = ''"
    >
      <template #activator="{ open }">
        <VAutocomplete
          ref="autocompleteRef"
          v-model="selectedTags"
          v-model:search="search"
          :items="items"
          :loading="tagsStore.isLoading[type]"
          :readonly="readonly || disabled || mobile"
          :label="$t('tags')"
          item-title="name"
          item-value="id"
          color="secondary"
          density="compact"
          autocomplete="off"
          class="flex-grow-1"
          multiple
          chips
          return-object
          no-filter
          hide-details
          hide-no-data
          :disabled="disabled"
          @update:focused="focused => focused && fetchTags()"
          @click="mobile && (open(), fetchTags())"
        >
          <template #item="{ item, props: itemProps }">
            <VListItem v-bind="itemProps">
              <template
                v-if="item.raw.id !== ADD_NEW"
                #prepend
              >
                <VCheckboxBtn
                  :model-value="selectedTags.some(tag => tag.id === item.raw.id)"
                  :ripple="false"
                  tabindex="-1"
                />
                <VIcon
                  :color="item.raw.color"
                  icon="ri-circle-fill"
                  size="13"
                  class="ms-1"
                />
              </template>
            </VListItem>
          </template>
          <template #chip="{ item }">
            <STag
              :text="item.raw.name"
              :color="item.raw.color"
              :closable="(!readonly && !disabled) || props.dontHideCloseBtnOnTags"
              @close="removeTag(item.raw.id)"
            />
          </template>
        </VAutocomplete>
      </template>
      <template #item="{ item, props: itemProps }">
        <VListItem v-bind="itemProps">
          <template
            v-if="item.raw.id !== ADD_NEW"
            #prepend
          >
            <VCheckboxBtn
              :model-value="selectedTags.some(tag => tag.id === item.raw.id)"
              :ripple="false"
              tabindex="-1"
            />
            <VIcon
              :color="item.raw.color"
              icon="ri-circle-fill"
              size="13"
              class="ms-1"
            />
          </template>
        </VListItem>
      </template>
    </MobileAutocomplete>

    <!-- Hidden once we know the user can't manage tags -->
    <VBtn
      v-if="!readonly && canCreate !== false"
      icon
      variant="flat"
      color="secondary"
      rounded="lg"
      size="40"
      :disabled="disabled || readonly"
      @click="openManager"
    >
      <VIcon
        size="20"
        icon="ri-price-tag-3-line"
      />
      <VTooltip
        activator="parent"
        location="top"
      >
        {{ $t('manage_tags') }}
      </VTooltip>
    </VBtn>
  </div>
</template>
